import classNames from 'classnames';
import { useMemo } from 'react';

import { PaymentInstrumentStripe, SubscriptionDetailsv4, SubscriptionPackage } from '@bpm-web-app/supreme-api-sdk';
import { useGetMeSubscription, useGetPaymentInstrument } from '@bpm-web-app/swr-hooks';
import { formatDateToString } from '@bpm-web-app/utils';
import Link from 'next/link';
import IconPayment from '../../components/icons/payment.svg';
import accountStyles from '../account-overview.module.css';
import styles from './your-plan.module.css';
import { Button } from '../../components/button/button';
import { StatusNote } from './status-note';
import { SubscriptionStatus } from './subscription-status';
import { ResumeButton } from './resume-button/resume-button';
import { usePlatform } from '../../helpers/usePlatform';
import { ResubscribeButton } from './resume-button/resubscribe-button';

const options = {
    [SubscriptionPackage.LibraryEnum.Music]: 'Open Format',
    [SubscriptionPackage.LibraryEnum.Latin]: 'Latino',
};

export const YourPlan = () => {
    const platform = usePlatform();
    const { data: userSubscription } = useGetMeSubscription(platform);
    const { data: paymentInstrument } = useGetPaymentInstrument(platform);
    const { style, membership, subscription } = useMemo(() => {
        const userMembership = userSubscription?.data.membership;
        const subscr = userMembership?.subscription;
        const subPkg = subscr?.package;

        if (subPkg) {
            const theme = (subPkg?.library === SubscriptionPackage.LibraryEnum.All
                ? 'all'
                : subPkg?.name ?? '')
                .toString().toLowerCase();

            return {
                style: {
                    '--color-subscription': `var(--color-subscription-${(theme)})`
                } as React.CSSProperties,
                subscription: subscr,
                membership: userMembership
            };
        }
        return { style: {} as React.CSSProperties, subscription: null, membership: userMembership };
    }, [userSubscription]);

    return (
        <section
            {...{ style }}
            className={classNames(accountStyles['account__section'], styles['plan-container'])}
        >
            <div className={styles['plan__heading']}>
                <div>
                    <h2 className={classNames(accountStyles['account__section-title'], styles['plan__title'])}>Your plan</h2>
                    <p className={classNames(accountStyles['account__section-title'], styles['plan__plan-title'])}>
                        <span>
                            {subscription?.package?.display_title || subscription?.package?.name || 'Free Plan'}
                        </span> {subscription?.library === SubscriptionPackage.LibraryEnum.All ? '' : !!subscription?.library && options[subscription?.library]}
                    </p>
                </div>

                <div className={styles['plan__status-wrapper']}>
                    <SubscriptionStatus membership={membership} status={subscription?.status} />
                    <StatusNote membership={membership} />
                </div>
            </div>

            <div className={styles['plan__footer']}>
                <div>
                    <p className={styles['plan__price']}>
                        ${subscription?.package?.amount ?? '0.00'}/
                        {subscription?.renewal_interval === 1 || !membership?.has_membership ? 'mo' : 'yr'}
                        {!!membership?.discounted && '*'}
                    </p>

                    {!!subscription?.next_billing && (
                        <ul className={styles['plan__meta']}>
                            {membership?.subscription?.status !== SubscriptionDetailsv4.StatusEnum.Cancelled &&
                                (
                                    <li>
                                        Next payment {formatDateToString(subscription.next_billing, 'L')}
                                    </li>
                                )
                            }
                            {!!paymentInstrument && (
                                <li>
                                    <IconPayment /> {(paymentInstrument?.data as PaymentInstrumentStripe).description}
                                </li>
                            )}
                        </ul>
                    )}
                </div>

                {membership?.has_membership ? (
                    <div className={styles['plan__actions']}>
                        {membership.subscription?.status === SubscriptionDetailsv4.StatusEnum.Cancelled && !membership.subscription.next_cycle_package_change ? (
                            <ResubscribeButton membership={membership} />
                        ) : (
                            <>
                                <Link href={`./plan/cancel/${subscription?.package_id}?platform=${platform}`} passHref>
                                    <Button type="button" variant="secondary" className={styles['plan__button']}>
                                        Cancel
                                    </Button>
                                </Link>
                                {membership.is_paused || membership.paused_until ? (
                                    <ResumeButton
                                        paymentInstrument={paymentInstrument?.data}
                                        membership={membership}
                                    />
                                ) : !membership.subscription?.next_cycle_package_change && (
                                    <Link href="./plan" passHref>
                                        <Button as="a" className={styles['plan__button']}>
                                            Change Plan
                                        </Button>
                                    </Link>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <div className={styles['plan__actions']}>
                        <Link href="./plan" passHref>
                            <Button as="a" className={styles['plan__button']}>
                                See Plans
                            </Button>
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
};
